import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchFollowers, fetchFollowing } from '../../services/socialService';

const PAGE_SIZE = 10;

function ProfileConnectionsView({ type = 'followers', createdProfile }) {
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [listData, setListData] = useState({ items: [], total: 0, limit: PAGE_SIZE });
  const [isLoading, setIsLoading] = useState(false);

  const isFollowers = type === 'followers';
  const title = isFollowers ? 'Followers' : 'Following';

  useEffect(() => {
    const profileUserId = createdProfile?.user;
    if (!profileUserId) return;

    let isMounted = true;

    const loadList = async () => {
      setIsLoading(true);
      try {
        const response = isFollowers
          ? await fetchFollowers(profileUserId, page, PAGE_SIZE)
          : await fetchFollowing(profileUserId, page, PAGE_SIZE);

        if (!isMounted) return;

        setListData({
          items: Array.isArray(response?.items) ? response.items : [],
          total: Number(response?.total) || 0,
          limit: Number(response?.limit) || PAGE_SIZE,
        });
      } catch (error) {
        if (!isMounted) return;
        setListData({ items: [], total: 0, limit: PAGE_SIZE });
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadList();

    return () => {
      isMounted = false;
    };
  }, [createdProfile?.user, isFollowers, page]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(listData.total / listData.limit)),
    [listData.limit, listData.total]
  );

  return (
    <div className="row g-4 justify-content-center">
      <div className="col-xl-10">
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h2 className="h5 mb-1">{title}</h2>
                <p className="text-muted mb-0">People in your {title.toLowerCase()} list.</p>
              </div>
              <Link className="btn btn-outline-secondary" to="/profile" state={{ from: location.pathname }}>
                Back to profile
              </Link>
            </div>

            {isLoading ? (
              <p className="text-muted mb-0">Loading {title.toLowerCase()}...</p>
            ) : listData.items.length ? (
              <>
                <ul className="list-group mb-3">
                  {listData.items.map((item) => {
                    const displayName = item.user?.name || item.user?.email || 'Unknown user';
                    return (
                      <li key={item.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <span>{displayName}</span>
                        <span className="badge text-bg-light border text-muted text-uppercase">{item.user?.role || 'user'}</span>
                      </li>
                    );
                  })}
                </ul>

                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">Page {page} of {totalPages}</small>
                  <div className="btn-group">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      disabled={page <= 1}
                      onClick={() => setPage((current) => Math.max(1, current - 1))}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      disabled={page >= totalPages}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-muted mb-0">No {title.toLowerCase()} found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileConnectionsView;
